"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { useInView } from "react-intersection-observer"
import { extractProperty, Link } from "utils"

import ArrowDown from "ui/icons/ArrowDown"
import Form from "./Form"
import LandingMedia from "./LandingMedia"

const StyledLayout = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background-color: white;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-height: unset;
    height: 100vh;
    flex-direction: row;
    align-items: center;
    padding-inline-start: 24px;
    gap: 40px;
    overflow: hidden;
  }
`

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px 16px;
  z-index: 2;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1;
    min-width: 0;
    align-self: stretch;
    overflow-y: auto;
    padding: 24px 40px;
    padding-left: 0px;
    padding-bottom: 24px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 40px 60px;
    padding-left: 0px;
    padding-bottom: 40px;
  }
`

const HeroImageContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: none;
    width: min(calc(91.67vw - 370px), 1390px);
    height: 100%;
    overflow: hidden;
    align-self: stretch;
    z-index: 1;
    padding: 24px 0;
  }
`

const ScrollCTA = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 16px;
  background-color: white;
  border-radius: 12px 12px 0 0;
  box-shadow: 0px 12px 25px 0px rgba(38, 48, 61, 0.32);
  margin-top: -80px;
  z-index: 10;
  position: fixed;
  bottom: 0;
  left: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
  button {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
  }
`

export default function DesignD({
  data,
  locale,
  tooltipText,
}: {
  data: any
  locale: string
  tooltipText: string
}) {
  const imageForDesktop = extractProperty(data, "imageDesktop", true)?.[0]
  const imageForMobile =
    extractProperty(data, "imageMobile", true)?.[0] || imageForDesktop
  const contactButton = extractProperty(data, "contactButton")?.[0]?.name
  const form = extractProperty(data, "form")
  const formTitle = extractProperty(form?.[0], "formTitle")
  const buttonText = extractProperty(form?.[0], "buttonText")
  const anchorId = extractProperty(form?.[0], "anchorId")
  const legalText =
    extractProperty(data, "legalText") ||
    extractProperty(form?.[0], "termsConfirmationText")

  const { ref: formRef, inView: isFormVisible } = useInView({
    threshold: 0.1,
  })

  const scrollToForm = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const formElement = document.getElementById(anchorId || "form")
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  return (
    <StyledLayout>
      <HeroImageContainer>
        <LandingMedia
          imageForDesktop={imageForDesktop}
          imageForMobile={imageForMobile}
        />
        <ScrollCTA style={{ display: isFormVisible ? "none" : undefined }}>
          <Button
            variant="primary"
            size="large"
            fullWidth
            onClick={scrollToForm}
            LinkComponent={Link}
          >
            <Typography variant="body1" fontWeight={700} color="white">
              {contactButton || "מלאו פרטים"}
            </Typography>
            <ArrowDown color="currentColor" aria-hidden="true" />
          </Button>
        </ScrollCTA>
      </HeroImageContainer>

      <FormColumn ref={formRef}>
        <Form
          formTitle={formTitle}
          designD={true}
          legalText={legalText}
          tooltipText={tooltipText}
          buttonText={buttonText}
          anchorId={anchorId}
        />
      </FormColumn>
    </StyledLayout>
  )
}
